import { Reveal } from "@/components/vision/reveal";
import { cn } from "@/lib/utils";

type EditorialHeadingProps = {
  index?: string;
  eyebrow: string;
  title: string;
  subtitle?: string;
  align?: "center" | "left";
  className?: string;
};

/**
 * Shared editorial-magazine section header.
 * Numbered kicker + hairline rule, Playfair display title, refined subtitle.
 * Presentation-only; text arrives via i18n.
 */
export function EditorialHeading({
  index,
  eyebrow,
  title,
  subtitle,
  align = "center",
  className,
}: EditorialHeadingProps) {
  const isCenter = align === "center";
  return (
    <Reveal
      className={cn(
        "max-w-2xl",
        isCenter ? "mx-auto text-center" : "text-left",
        className,
      )}
      y={28}
      blur={10}
    >
      <div
        className={cn(
          "flex items-center gap-3",
          isCenter ? "justify-center" : "justify-start",
        )}
      >
        {index && (
          <span className="font-editorial text-sm italic text-accent">{index}</span>
        )}
        <span className="h-px w-8 bg-border" aria-hidden />
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {eyebrow}
        </p>
      </div>
      <h2 className="mt-4 font-editorial text-[30px] font-semibold leading-[1.1] tracking-[-0.01em] text-foreground sm:text-4xl md:text-5xl text-balance">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-[15px] text-muted-foreground sm:mt-5 sm:text-lg text-balance">
          {subtitle}
        </p>
      )}
    </Reveal>
  );
}
